/**
 * Direct wallet buttons — Stripe's Express Checkout Element (GH-240, owner-directed 2026-08-17).
 *
 * Tapping Apple Pay or Google Pay here opens the *wallet sheet itself*, not a card form: the
 * element is mounted in deferred-intent mode with the quoted total, and the server-side
 * PaymentIntent is created only inside the wallet's confirm step. The order of operations is
 * Stripe's required one — validate (`elements.submit()`), then mint the intent, then
 * `confirmPayment` against its client secret — and the displayed total the donor authorizes in
 * the wallet is the same signed quote every other path charges. No wallet contact details are
 * requested (this product needs none), and the intent amount is the server's, never this file's.
 *
 * The element renders nothing on a device with no wallet, and this component says so to its
 * parent via `onAvailability`, so the sheet can lay itself out honestly.
 */
import type { Stripe, StripeElements, StripeElementsOptionsMode } from "@stripe/stripe-js";
import { useEffect, useRef, useState } from "react";
import { stripeClient } from "./StripeConfirm.js";
import { donorMessageForStripeError } from "./card-failure-message.js";

export type WalletMethod = "apple_pay" | "google_pay";

/**
 * Deferred-intent Elements for the wallet buttons. `paymentMethodTypes` must name the server's
 * types: without it Elements runs in automatic-payment-methods mode, and Stripe refuses to confirm
 * it against our PaymentIntent, which is created with `payment_method_types: ["card"]`
 * (`buildIntentParams` in packages/payments). Apple Pay and Google Pay ride the `card` type.
 */
export function walletElementsOptions(amountMinor: string, currency: string) {
  return {
    mode: "payment",
    amount: Number(amountMinor),
    currency: currency.toLowerCase(),
    paymentMethodTypes: ["card"],
    appearance: { variables: { colorPrimary: "#0a6b4f", borderRadius: "14px" } },
  } satisfies StripeElementsOptionsMode;
}

export function WalletExpressBoxes({
  amountMinor,
  currency,
  createIntent,
  onAuthorized,
  onFailed,
  onAvailability,
  applePay = "auto",
  googlePay = "auto",
}: {
  /**
   * `never` inside a frame on another site (GH-847): Apple Pay there needs the host page's domain
   * registered with Apple, which no host page is, so the framed give box sends Apple Pay to a
   * first-party GiveHere window instead (`EmbedWallet`). Everywhere else, `auto`.
   */
  applePay?: "auto" | "never";
  /**
   * `never` where this element is the give box's leading Apple Pay button (GH-856): Google Pay is
   * still one tap away in the payment sheet, and two wallets where one button stood is a choice
   * the donor did not ask for. Everywhere else, `auto`.
   */
  googlePay?: "auto" | "never";
  /** The quoted total in minor units — must equal what the server will put on the intent. */
  amountMinor: string;
  currency: string;
  /** Mint the server-side intent mid-confirm; resolves to its client secret. */
  createIntent: (method: WalletMethod) => Promise<{ clientSecret: string | null }>;
  /** Authorization finished (succeeded or processing); the receipt screen takes it from here. */
  onAuthorized: () => void;
  /**
   * The wallet's authorization failed *after* the server opened the payment (GH-809). The caller
   * starts the next press as a new attempt — a fresh idempotency key — so a retry is never a
   * replay of the request whose authorization just failed, and never meets a key bound to it.
   */
  onFailed?: () => void;
  /** Whether this device actually offers a wallet, once Stripe has answered. */
  onAvailability?: (available: boolean) => void;
}) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Latest callbacks in refs so the one-time mount effect never sees a stale closure.
  const createIntentRef = useRef(createIntent);
  createIntentRef.current = createIntent;
  const onAuthorizedRef = useRef(onAuthorized);
  onAuthorizedRef.current = onAuthorized;
  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;
  const onAvailabilityRef = useRef(onAvailability);
  onAvailabilityRef.current = onAvailability;

  useEffect(() => {
    let cancelled = false;
    let stripe: Stripe | null = null;
    let elements: StripeElements | null = null;

    void stripeClient().then((client) => {
      if (cancelled || !client || !mountRef.current) return;
      stripe = client;
      elements = client.elements(walletElementsOptions(amountMinor, currency));

      const element = elements.create("expressCheckout", {
        // Wallets only. Everything else a Stripe dashboard switch could enable stays off —
        // the same fail-closed posture as the server's explicit payment_method_types.
        paymentMethods: {
          applePay,
          googlePay,
          link: "never",
          amazonPay: "never",
          paypal: "never",
          klarna: "never",
        },
      });

      element.on("ready", (event) => {
        if (cancelled) return;
        onAvailabilityRef.current?.(event.availablePaymentMethods !== undefined);
      });

      element.on("confirm", (event) => {
        void (async () => {
          if (!stripe || !elements) return;
          setError(null);
          const submitted = await elements.submit();
          if (submitted.error) {
            setError(submitted.error.message ?? "The payment did not start.");
            return;
          }
          try {
            const method: WalletMethod =
              event.expressPaymentType === "apple_pay" ? "apple_pay" : "google_pay";
            const created = await createIntentRef.current(method);
            if (!created.clientSecret) {
              // Nothing to authorize (synthetic rail): the receipt takes over.
              onAuthorizedRef.current();
              return;
            }
            const result = await stripe.confirmPayment({
              elements,
              clientSecret: created.clientSecret,
              confirmParams: { return_url: window.location.href },
              redirect: "if_required",
            });
            if (result.error) {
              setError(donorMessageForStripeError(result.error));
              onFailedRef.current?.();
              return;
            }
            onAuthorizedRef.current();
          } catch {
            setError("The payment did not start.");
            onFailedRef.current?.();
          }
        })();
      });

      element.mount(mountRef.current);
    });

    return () => {
      cancelled = true;
      elements?.getElement("expressCheckout")?.destroy();
    };
    // Mounted once per (amount, currency): a changed total is a different authorization.
  }, [amountMinor, currency, applePay, googlePay]);

  return (
    <div className="wallet-express">
      <div ref={mountRef} />
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
